import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import NavBar from "../components/NavBar";
import { Trash2, Plus, Mail, Building2, BookMarked, Edit2, X, Download, Image as ImageIcon, Upload, FileDown, Sparkles, RefreshCw } from "lucide-react";
import { ForwardDialog, ForwardButton, ForwardedInfo } from "../components/ForwardDialog";
import { ScopeTabs, ArchiveButton, SendResponsesButton, isPast, BrandLine, NewAnswerBadge } from "../components/RequestTools";
import { StaySummary } from "../components/StayFields";
import { PhotoGrid } from "../components/PhotoGrid";
import { PdfButton, NeighborhoodFields } from "../components/ListingPdf";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { key: "emails", label: "Emails autorisés", Icon: Mail },
  { key: "listings", label: "Annonces", Icon: Building2 },
  { key: "reservations", label: "Demandes", Icon: BookMarked },
  { key: "custom", label: "Sur mesure", Icon: Sparkles },
];

const AREA_LABELS = { "centre-ville": "Centre-ville", "petite-couronne": "Petite couronne", "grande-couronne": "Grande couronne" };
const APT_LABELS = { studio: "Studio", t2: "2 pièces", t3: "3 pièces", t4plus: "4 pièces et plus" };

const STATUS_OPTIONS = [
  { v: "pending", l: "En attente" },
  { v: "sent", l: "Envoyée" },
  { v: "accepted", l: "Acceptée" },
  { v: "declined", l: "Refusée" },
  { v: "closed", l: "Clôturée" },
];

const EMPTY_LISTING = {
  title: "", type: "aparthotel", city: "", short_description: "", description: "",
  price_1m_month: "", min_duration_months: 1, max_duration_months: 12, capacity: 1,
  amenities: [], image_urls: [], contact_name: "", contact_email: "", contact_phone: "", active: true,
  area: "", apartment_type: "",
};

export default function AdminDashboard() {
  const [tab, setTab] = useState("emails");
  const [unseen, setUnseen] = useState({});
  useEffect(() => {
    const load = () => axios.get(`${API}/admin/unseen-counts`, { withCredentials: true }).then(({ data }) => setUnseen(data)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    window.addEventListener("unseen-changed", load);
    return () => { clearInterval(t); window.removeEventListener("unseen-changed", load); };
  }, [tab]);
  return (
    <div className="min-h-screen bg-[#F5F3ED]">
      <NavBar />
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <div className="text-xs tracking-[0.25em] uppercase font-semibold text-[#8C6C50]">Administration</div>
        <h1 className="font-serif text-4xl lg:text-5xl tracking-tighter mt-4 text-[#1A1A1A]">Tableau de bord</h1>

        <div className="mt-10 flex gap-2 border-b border-[#E5E1D8] overflow-x-auto" data-testid="admin-tabs">
          {TABS.map(({ key, label, Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`px-5 py-3 text-sm font-medium -mb-px border-b-2 inline-flex items-center gap-2 whitespace-nowrap ${
                tab === key ? "border-[#8C6C50] text-[#8C6C50]" : "border-transparent text-[#666] hover:text-[#1A1A1A]"
              }`}
              data-testid={`admin-tab-${key}`}
            >
              <Icon className="w-4 h-4" /> {label}
              {unseen[key] > 0 && <span className="ml-1 min-w-5 h-5 px-1.5 rounded-full bg-[#C2410C] text-white text-[11px] font-semibold inline-flex items-center justify-center" data-testid={`admin-tab-${key}-unseen`}>{unseen[key]}</span>}
            </button>
          ))}
        </div>

        <div className="mt-10">
          {tab === "emails" && <AuthorizedEmailsPanel />}
          {tab === "listings" && <ListingsPanel />}
          {tab === "reservations" && <ReservationsPanel />}
          {tab === "custom" && <CustomRequestsPanel />}
        </div>
      </section>
    </div>
  );
}

function AuthorizedEmailsPanel() {
  const [rows, setRows] = useState([]);
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");

  const load = async () => {
    const { data } = await axios.get(`${API}/admin/authorized-emails`, { withCredentials: true });
    setRows(data);
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/authorized-emails`, { email, note }, { withCredentials: true });
      toast.success("Email ajouté");
      setEmail(""); setNote("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Retirer cet email ?")) return;
    await axios.delete(`${API}/admin/authorized-emails/${id}`, { withCredentials: true });
    load();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
      <div className="lg:col-span-1">
        <h3 className="font-serif text-2xl text-[#1A1A1A]">Autoriser un email</h3>
        <p className="mt-2 text-sm text-[#666]">Seuls les emails de cette liste peuvent créer un compte.</p>
        <form onSubmit={add} className="mt-6 space-y-3 bg-white p-6 border border-[#E5E1D8] rounded-md">
          <input
            type="email" required placeholder="personne@exemple.fr"
            value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2.5 bg-[#F5F3ED] border border-[#E5E1D8] rounded-md text-sm"
            data-testid="admin-email-input"
          />
          <input
            type="text" placeholder="Note (optionnel)" value={note}
            onChange={(e) => setNote(e.target.value)}
            className="w-full px-3 py-2.5 bg-[#F5F3ED] border border-[#E5E1D8] rounded-md text-sm"
            data-testid="admin-email-note"
          />
          <button type="submit" className="btn-primary w-full py-2.5 rounded-md text-sm font-medium inline-flex items-center justify-center gap-2" data-testid="admin-email-add-btn">
            <Plus className="w-4 h-4" /> Ajouter
          </button>
        </form>
      </div>
      <div className="lg:col-span-2">
        <h3 className="font-serif text-2xl text-[#1A1A1A]">Emails autorisés ({rows.length})</h3>
        <div className="mt-6 bg-white border border-[#E5E1D8] rounded-md divide-y divide-[#E5E1D8]" data-testid="admin-emails-list">
          {rows.map((r) => (
            <div key={r.id} className="p-4 flex items-center justify-between">
              <div>
                <div className="text-sm text-[#1A1A1A] font-medium">{r.email}</div>
                {r.note && <div className="text-xs text-[#666] mt-0.5">{r.note}</div>}
              </div>
              <button onClick={() => remove(r.id)} className="p-2 rounded-md hover:bg-rose-50 text-rose-600" title="Supprimer" data-testid={`admin-email-delete-${r.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          {rows.length === 0 && <div className="p-6 text-sm text-[#666]">Aucun email autorisé pour l'instant.</div>}
        </div>
      </div>
    </div>
  );
}

function ImportReport({ report, onClose }) {
  if (!report) return null;
  const bad = report.fatal || report.errors > 0;
  return (
    <div className={`mt-4 p-4 rounded-md border text-sm ${bad ? "bg-[#FBF1EE] border-[#E8C9BE]" : "bg-[#EEF3EC] border-[#CFDCCB]"}`} data-testid="import-report">
      <div className="flex items-start justify-between gap-4">
        <div className="font-medium text-[#1A1A1A]" data-testid="import-report-summary">
          {report.fatal
            ? `Import refusé : ${report.fatal}`
            : `${report.created} créée(s), ${report.updated} mise(s) à jour, ${report.errors} ligne(s) refusée(s)`}
          {report.enriching > 0 && (
            <div className="mt-1 font-normal text-[#2B3029]" data-testid="import-report-enriching">
              {report.enriching} annonce(s) en cours de récupération depuis le site partenaire (photos, équipements, description). Le statut s'affiche sur chaque annonce ci-dessous.
            </div>
          )}
        </div>
        <button onClick={onClose} className="p-1 hover:bg-black/5 rounded" data-testid="import-report-close"><X className="w-4 h-4" /></button>
      </div>
      {report.error_rows?.length > 0 && (
        <ul className="mt-3 space-y-1.5" data-testid="import-report-errors">
          {report.error_rows.map((r) => (
            <li key={r.row} className="text-[#7A3B2A]">
              <strong>Ligne {r.row}</strong> ({r.title}) : {r.error}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function DeleteAllButton({ count, onDone }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/listings/delete-all`, { confirm: text }, { withCredentials: true });
      toast.success(`${data.deleted} annonce(s) supprimée(s)`);
      setOpen(false);
      setText("");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Suppression impossible");
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <button onClick={() => setOpen(true)} className="px-4 py-2.5 rounded-full text-sm font-medium border border-rose-200 text-rose-700 bg-white hover:bg-rose-50 inline-flex items-center gap-2" data-testid="admin-listings-delete-all-btn">
        <Trash2 className="w-4 h-4" /> Tout supprimer
      </button>
      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="delete-all-dialog">
          <div className="bg-white rounded-md w-full max-w-md p-6">
            <h3 className="font-serif text-2xl text-[#1A1A1A]">Supprimer toutes les annonces ?</h3>
            <p className="text-sm text-[#666] mt-2">
              Les {count} annonces du catalogue seront définitivement supprimées. Les demandes déjà reçues sont conservées. Pensez à exporter le CSV avant. Tapez <strong>SUPPRIMER</strong> pour confirmer.
            </p>
            <input value={text} onChange={(e) => setText(e.target.value)} className={`${inp} mt-4`} placeholder="SUPPRIMER" data-testid="delete-all-confirm-input" />
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => { setOpen(false); setText(""); }} className="px-4 py-2.5 rounded-full text-sm border border-[#E5E1D8]" data-testid="delete-all-cancel-btn">Annuler</button>
              <button onClick={run} disabled={busy || text.trim().toUpperCase() !== "SUPPRIMER"} className="px-5 py-2.5 rounded-full text-sm font-medium bg-rose-600 text-white disabled:opacity-40" data-testid="delete-all-confirm-btn">
                {busy ? "Suppression…" : "Supprimer tout"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function SourceBadge({ l }) {
  if (!l.source_status) return null;
  const map = {
    pending: ["bg-[#F5EFE6] text-[#8C6C50]", "Récupération depuis le site partenaire…"],
    ok: ["bg-[#EEF3EC] text-[#4F6B4A]", `Contenu récupéré · ${(l.image_urls || []).length} photo(s)`],
    error: ["bg-[#FBF1EE] text-[#7A3B2A]", "Récupération impossible"],
  };
  const [cls, label] = map[l.source_status] || map.error;
  return (
    <div className={`mt-2 text-xs px-2 py-1 rounded inline-block ${cls}`} data-testid={`listing-source-status-${l.id}`}>
      {label}{l.source_error ? ` — ${l.source_error}` : ""}
      {!l.active && l.source_status !== "pending" && " · annonce masquée, à compléter"}
    </div>
  );
}

function ListingsPanel() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);
  const [report, setReport] = useState(null);
  const [filters, setFilters] = useState({ city: "", brand: "", department: "", apartment_type: "", type: "" });
  const shown = rows.filter((l) => Object.entries(filters).every(([k, v]) => !v || (l[k] || "").toString().trim().toLowerCase() === v.toLowerCase()));
  const filtered = Object.values(filters).some(Boolean);
  const pending = rows.some((l) => l.source_status === "pending");

  useEffect(() => {
    if (!pending) return undefined;
    const t = setInterval(() => { load(); }, 4000);
    return () => { clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending]);

  const load = async () => {
    const { data } = await axios.get(`${API}/admin/listings`, { withCredentials: true });
    setRows(data);
  };
  useEffect(() => { load(); }, []);

  const save = async (payload) => {
    try {
      if (payload.id) {
        await axios.patch(`${API}/admin/listings/${payload.id}`, payload, { withCredentials: true });
        toast.success("Annonce mise à jour");
      } else {
        await axios.post(`${API}/admin/listings`, payload, { withCredentials: true });
        toast.success("Annonce créée");
      }
      setEditing(null);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Enregistrement impossible, vérifiez les champs de l'annonce", { duration: 10000 });
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette annonce ?")) return;
    await axios.delete(`${API}/admin/listings/${id}`, { withCredentials: true });
    toast.success("Annonce supprimée");
    load();
  };

  const refreshSource = async (id) => {
    try {
      await axios.post(`${API}/admin/listings/${id}/refresh-source`, {}, { withCredentials: true });
      toast.success("Récupération relancée");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de relancer la récupération");
    }
  };

  const exportCsv = async () => {
    try {
      const resp = await axios.get(`${API}/admin/listings/export`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, "");
      a.href = url;
      a.download = `matchappart-annonces-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export téléchargé");
    } catch (e) {
      toast.error("Export impossible");
    }
  };

  const importCsv = async (file) => {
    if (!file) return;
    setReport(null);
    setImporting(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await axios.post(`${API}/admin/listings/import`, form, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      let msg = `${data.created} créée(s), ${data.updated} mise(s) à jour`;
      if (data.errors > 0) {
        toast.error(`${msg} · ${data.errors} ligne(s) refusée(s), voir le détail`);
      } else {
        toast.success(msg);
      }
      setReport(data);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      const detail = typeof d === "string" ? d : "Fichier illisible. Enregistrez-le au format CSV UTF-8 (séparateur ; ou ,).";
      toast.error(detail);
      setReport({ fatal: detail });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-serif text-2xl text-[#1A1A1A]">Annonces ({filtered ? `${shown.length}/${rows.length}` : rows.length})</h3>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={exportCsv}
            className="px-4 py-2.5 rounded-full text-sm font-medium border border-[#E5E1D8] bg-white hover:bg-[#F5F3ED] inline-flex items-center gap-2"
            data-testid="admin-listings-export-btn"
          >
            <FileDown className="w-4 h-4" /> Exporter CSV
          </button>
          <label className="px-4 py-2.5 rounded-full text-sm font-medium border border-[#E5E1D8] bg-white hover:bg-[#F5F3ED] inline-flex items-center gap-2 cursor-pointer" data-testid="admin-listings-import-label">
            <Upload className="w-4 h-4" /> {importing ? "Import…" : "Importer CSV"}
            <input
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => { importCsv(e.target.files?.[0]); e.target.value = ""; }}
              data-testid="admin-listings-import-input"
            />
          </label>
          <button
            onClick={() => setEditing({ ...EMPTY_LISTING })}
            className="btn-primary px-5 py-2.5 rounded-full text-sm font-medium inline-flex items-center gap-2"
            data-testid="admin-listing-new-btn"
          >
            <Plus className="w-4 h-4" /> Nouvelle annonce
          </button>
          {rows.length > 0 && <DeleteAllButton count={rows.length} onDone={load} />}
        </div>
      </div>
      <p className="mt-2 text-xs text-[#666]">
        Exportez pour éditer dans Excel/Numbers. À l'import, une annonce existante est mise à jour (et non dupliquée) si la ligne a le même <code>id</code>, sinon la même <code>source_url</code>, sinon le même titre + ville (+ enseigne et nom de résidence si les colonnes <code>brand</code> / <code>residence_name</code> sont présentes) ; sinon elle est créée. Séparateur <code>;</code>, listes séparées par <code>|</code>.
        Obligatoire sur chaque ligne : au moins un tarif (<code>price_1m_month</code>, <code>price_1m_night</code>, <code>price_3m_month</code> ou <code>price_3m_night</code>), <code>contact_email</code> (email valide), ainsi que <code>title</code> et <code>city</code> sauf si <code>source_url</code> est renseigné.
        Colonne <code>type</code> obligatoire : <code>Apparthôtel</code>, <code>Student</code> ou <code>Sénior</code> uniquement (majuscules et accents indifférents), sinon la ligne est refusée.
        Colonnes facultatives <code>address</code> et <code>postal_code</code> : affichées sur l'annonce et la fiche PDF ; le département (filtre du catalogue) est déduit du code postal. Si <code>apartment_type</code> est vide, il est déduit du titre (Studio, 2 pièces…). Colonnes facultatives <code>neighborhood_ambiance</code>, <code>neighborhood_shops</code>, <code>neighborhood_transport</code> (textes du quartier).
        Colonne facultative <code>source_url</code> : l'adresse de l'annonce chez le partenaire ; photos, équipements et description y sont récupérés automatiquement (titre et ville aussi s'ils sont vides).
        Réimport : les photos déjà présentes sur une annonce sont toujours conservées ; les nouvelles photos du fichier (<code>image_urls</code>) sont ajoutées à la suite.
      </p>
      <ImportReport report={report} onClose={() => setReport(null)} />
      <ListingFilters rows={rows} filters={filters} setFilters={setFilters} />

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="admin-listings-list">
        {shown.map((l) => (
          <div key={l.id} className="bg-white border border-[#E5E1D8] rounded-md p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[11px] tracking-[0.2em] uppercase font-semibold text-[#8C6C50]">{[l.postal_code, l.city].filter(Boolean).join(" ")}</div>
                <div className="font-serif text-xl mt-1 text-[#1A1A1A]">{l.title || "(titre à compléter)"}</div>
                <div className="mt-1 text-xs text-[#666]">{l.brand ? `${l.brand} · ` : ""}{l.residence_name ? `${l.residence_name} · ` : ""}Contact : {(l.contact_emails?.length ? l.contact_emails : [l.contact_email]).join(", ")}</div>
                <SourceBadge l={l} />
              </div>
              <div className="flex gap-1 items-start">
                <PdfButton id={l.id} withPriceOption testid={`admin-listing-pdf-${l.id}`} />
                {l.source_url && (
                  <button
                    onClick={() => refreshSource(l.id)}
                    disabled={l.source_status === "pending"}
                    title="Relancer la récupération depuis le site partenaire"
                    className="p-2 rounded-md hover:bg-[#EFECE5] disabled:opacity-40"
                    data-testid={`admin-listing-refresh-source-${l.id}`}
                  >
                    <RefreshCw className={`w-4 h-4 ${l.source_status === "pending" ? "animate-spin" : ""}`} />
                  </button>
                )}
                <button onClick={() => setEditing(l)} className="p-2 rounded-md hover:bg-[#EFECE5]" data-testid={`admin-listing-edit-${l.id}`}>
                  <Edit2 className="w-4 h-4" />
                </button>
                <button onClick={() => remove(l.id)} className="p-2 rounded-md hover:bg-rose-50 text-rose-600" data-testid={`admin-listing-delete-${l.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="mt-3 text-sm text-[#2B3029]">{PRICE_FIELDS.filter(([k]) => l[k] != null).map(([k]) => `${k.includes("3m") ? "3 mois" : "1 mois"} : ${Number(l[k]).toLocaleString("fr-FR")} € / ${k.endsWith("night") ? "nuit" : "mois"}`).join(" · ") || "Aucun tarif"}</div>
            <div className="text-xs text-[#666] mt-0.5">{l.capacity} pers. · dès {l.min_duration_months} mois</div>
          </div>
        ))}
      </div>

      {editing && <ListingEditor initial={editing} onSave={save} onClose={() => setEditing(null)} />}
    </div>
  );
}

const RTYPE_LABELS = { aparthotel: "Apparthôtel", student: "Student", senior: "Sénior" };

const uniq = (rows, k) => [...new Set(rows.map((l) => (l[k] || "").toString().trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b, "fr"));

function ListingFilters({ rows, filters, setFilters }) {
  const [deptLabels, setDeptLabels] = useState({});
  useEffect(() => {
    axios.get(`${API}/listings-meta/departments`, { withCredentials: true })
      .then(({ data }) => setDeptLabels(Object.fromEntries(data.map((d) => [d.code, d.label]))))
      .catch(() => {});
  }, []);
  const defs = [
    ["brand", "Toutes les enseignes", (v) => v],
    ["department", "Tous les départements", (v) => deptLabels[v] || v],
    ["city", "Toutes les villes", (v) => v],
    ["apartment_type", "Tous types d'appartements", (v) => APT_LABELS[v] || v],
    ["type", "Tous types de résidences", (v) => RTYPE_LABELS[v] || v],
  ];
  const any = Object.values(filters).some(Boolean);
  return (
    <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="admin-listings-filters">
      {defs.map(([k, all, label]) => (
        <select key={k} value={filters[k]} onChange={(e) => setFilters({ ...filters, [k]: e.target.value })} className={`px-4 py-2 rounded-full text-sm border bg-white ${filters[k] ? "border-[#8C6C50] text-[#8C6C50]" : "border-[#E5E1D8]"}`} data-testid={`admin-listings-${k.replace("_", "-")}-filter`}>
          <option value="">{all}</option>
          {uniq(rows, k).map((v) => <option key={v} value={v}>{label(v)}</option>)}
        </select>
      ))}
      {any && (
        <button type="button" onClick={() => setFilters({ city: "", brand: "", department: "", apartment_type: "", type: "" })} className="px-3 py-2 text-sm text-[#666] hover:text-[#1A1A1A] inline-flex items-center gap-1" data-testid="admin-listings-filters-reset">
          <X className="w-3.5 h-3.5" /> Réinitialiser
        </button>
      )}
    </div>
  );
}

const PRICE_FIELDS = [
  ["price_1m_month", "Tarif 1 mois (€ / mois)"],
  ["price_1m_night", "Tarif 1 mois (€ / nuit)"],
  ["price_3m_month", "Tarif 3 mois (€ / mois)"],
  ["price_3m_night", "Tarif 3 mois (€ / nuit)"],
];

function ContactEmails({ emails, onChange }) {
  const update = (i, val) => onChange(emails.map((e, j) => (j === i ? val : e)));
  return (
    <div data-testid="editor-contact-emails">
      <span className="text-xs tracking-[0.15em] uppercase font-semibold text-[#666]">Emails de contact * ({emails.length}/5)</span>
      <div className="mt-1.5 space-y-2">
        {emails.map((e, i) => (
          <div key={i} className="flex gap-2">
            <input
              type="email"
              required={i === 0}
              value={e}
              onChange={(ev) => update(i, ev.target.value)}
              placeholder={i === 0 ? "Email principal" : "Email supplémentaire"}
              className={inp}
              data-testid={i === 0 ? "editor-contact-email" : `editor-contact-email-${i}`}
            />
            {i > 0 && (
              <button type="button" onClick={() => onChange(emails.filter((_, j) => j !== i))} className="px-3 rounded-md border border-[#E5E1D8] hover:bg-rose-50 text-rose-600" data-testid={`editor-contact-email-remove-${i}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>
      {emails.length < 5 && (
        <button type="button" onClick={() => onChange([...emails, ""])} className="mt-2 text-sm text-[#8C6C50] inline-flex items-center gap-1 hover:underline" data-testid="editor-contact-email-add">
          <Plus className="w-4 h-4" /> Ajouter un email
        </button>
      )}
      <p className="text-xs text-[#888] mt-1">Les demandes transmises à la résidence sont envoyées à tous ces emails.</p>
    </div>
  );
}

function ListingEditor({ initial, onSave, onClose }) {
  const [v, setV] = useState({
    ...initial,
    amenities: Array.isArray(initial.amenities) ? initial.amenities : [],
    image_urls: Array.isArray(initial.image_urls) ? initial.image_urls : [],
    brand: initial.brand || "",
    residence_name: initial.residence_name || "",
    address: initial.address || "",
    postal_code: initial.postal_code || "",
    contact_emails: initial.contact_emails?.length ? initial.contact_emails : [initial.contact_email || ""],
  });
  const [importUrl, setImportUrl] = useState(initial.source_url || "");
  const [importing, setImporting] = useState(false);
  const [newAmenity, setNewAmenity] = useState("");
  const [newImageUrl, setNewImageUrl] = useState("");

  const set = (k, val) => setV((s) => ({ ...s, [k]: val }));

  const importFromUrl = async () => {
    if (!importUrl.trim()) return;
    setImporting(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/listings/scrape`,
        { url: importUrl.trim() },
        { withCredentials: true }
      );
      setV((s) => ({
        ...s,
        title: s.title || data.title || "",
        city: s.city || data.city || "",
        short_description: s.short_description || data.short_description || "",
        description: s.description || data.description || "",
        amenities: [...(s.amenities || []), ...data.amenities.filter((a) => !(s.amenities || []).includes(a))],
        image_urls: [...(s.image_urls || []), ...data.image_urls.filter((u) => !(s.image_urls || []).includes(u))],
        source_url: data.source_url,
      }));
      if (data.image_urls.length === 0) {
        toast.warning("Aucune photo détectée sur cette page. Essayez l'URL d'une page interne (ex. la page de l'appartement précis).");
      } else {
        toast.success(`Import réussi : ${data.image_urls.length} photo(s), ${data.amenities.length} équipement(s)`);
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Impossible d'importer cette URL";
      toast.error(msg, { duration: 8000 });
    } finally {
      setImporting(false);
    }
  };

  const addAmenity = () => {
    const t = newAmenity.trim();
    if (t && !v.amenities.includes(t)) {
      set("amenities", [...v.amenities, t]);
      setNewAmenity("");
    }
  };
  const removeAmenity = (a) => set("amenities", v.amenities.filter((x) => x !== a));

  const addImage = () => {
    const t = newImageUrl.trim();
    if (t && !v.image_urls.includes(t)) {
      set("image_urls", [...v.image_urls, t]);
      setNewImageUrl("");
    }
  };
  const [uploading, setUploading] = useState(false);
  const uploadImages = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      const form = new FormData();
      files.forEach((f) => form.append("files", f));
      const { data } = await axios.post(`${API}/admin/uploads`, form, { withCredentials: true });
      setV((s) => ({ ...s, image_urls: [...s.image_urls, ...data.image_urls] }));
      toast.success(`${data.image_urls.length} photo(s) ajoutée(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Téléversement impossible");
    } finally {
      setUploading(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    onSave({
      ...v,
      monthly_price: undefined,
      min_duration_months: Number(v.min_duration_months),
      max_duration_months: v.max_duration_months ? Number(v.max_duration_months) : null,
      capacity: Number(v.capacity),
      area: v.area || null,
      apartment_type: v.apartment_type || null,
      amenities: v.amenities,
      image_urls: v.image_urls,
      ...Object.fromEntries(PRICE_FIELDS.map(([k]) => [k, v[k] === "" || v[k] == null ? null : Number(v[k])])),
      contact_emails: v.contact_emails.map((x) => x.trim()).filter(Boolean),
      contact_email: v.contact_emails.map((x) => x.trim()).filter(Boolean)[0] || null,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="admin-listing-editor">
      <div className="bg-white rounded-md w-full max-w-4xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-[#E5E1D8] sticky top-0 bg-white z-10">
          <h3 className="font-serif text-2xl text-[#1A1A1A]">{v.id ? "Modifier l'annonce" : "Nouvelle annonce"}</h3>
          <button onClick={onClose} className="p-2 hover:bg-[#EFECE5] rounded-md" data-testid="admin-editor-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {/* Import from URL */}
          <div className="bg-[#F5F3ED] border border-[#E5E1D8] rounded-md p-5 mb-6">
            <div className="flex items-center gap-2 mb-2">
              <Download className="w-4 h-4 text-[#8C6C50]" />
              <h4 className="font-serif text-lg text-[#1A1A1A]">Importer depuis l'URL d'une résidence</h4>
            </div>
            <p className="text-xs text-[#666]">
              Collez l'URL de la page officielle de l'hébergement. Nous récupérons titre, description,
              photos et équipements. Pour un meilleur résultat, utilisez la page du logement précis
              (pas la page d'accueil du site). Si un site bloque malgré tout l'import, téléversez les
              photos depuis votre ordinateur ou collez leurs liens plus bas dans la galerie.
            </p>
            <div className="mt-3 flex gap-2">
              <input
                type="url"
                placeholder="https://..."
                value={importUrl}
                onChange={(e) => setImportUrl(e.target.value)}
                className="flex-1 px-3 py-2.5 bg-white border border-[#E5E1D8] rounded-md text-sm focus:outline-none focus:border-[#8C6C50]"
                data-testid="editor-import-url"
              />
              <button
                type="button"
                disabled={importing}
                onClick={importFromUrl}
                className="btn-primary px-5 py-2.5 rounded-md text-sm font-medium disabled:opacity-60"
                data-testid="editor-import-btn"
              >
                {importing ? "Import…" : "Importer"}
              </button>
            </div>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Titre"><input required value={v.title} onChange={(e) => set("title", e.target.value)} className={inp} data-testid="editor-title" /></Field>
              <Field label="Catégorie (interne, non affichée)">
                <select value={v.type} onChange={(e) => set("type", e.target.value)} className={inp} data-testid="editor-type">
                  <option value="aparthotel">Appart'hotel</option>
                  <option value="student">Résidence étudiante</option>
                  <option value="senior">Résidence sénior</option>
                </select>
              </Field>
              <Field label="Enseigne (affichée sur l'annonce)"><input value={v.brand} maxLength={120} onChange={(e) => set("brand", e.target.value)} placeholder="Ex. Adagio" className={inp} data-testid="editor-brand" /></Field>
              <Field label="Ville"><input required value={v.city} onChange={(e) => set("city", e.target.value)} className={inp} data-testid="editor-city" /></Field>
              <Field label="Nom de la résidence (affiché)"><input value={v.residence_name} maxLength={160} onChange={(e) => set("residence_name", e.target.value)} placeholder="Ex. Adagio Lyon Patio Confluence" className={inp} data-testid="editor-residence-name" /></Field>
              <Field label="Adresse (affichée)"><input value={v.address} maxLength={300} onChange={(e) => set("address", e.target.value)} placeholder="Ex. 27 rue Smith" className={inp} data-testid="editor-address" /></Field>
              <Field label="Code postal (affiché)"><input value={v.postal_code} maxLength={10} onChange={(e) => set("postal_code", e.target.value)} placeholder="Ex. 92500" className={inp} data-testid="editor-postal-code" /></Field>
              <Field label="Emplacement">
                <select value={v.area || ""} onChange={(e) => set("area", e.target.value)} className={inp} data-testid="editor-area">
                  <option value="">Non renseigné</option>
                  {Object.entries(AREA_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </Field>
              <Field label="Type d'appartement">
                <select value={v.apartment_type || ""} onChange={(e) => set("apartment_type", e.target.value)} className={inp} data-testid="editor-apartment-type">
                  <option value="">Non renseigné</option>
                  {Object.entries(APT_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </Field>
              <Field label="Capacité"><input type="number" min={1} required value={v.capacity} onChange={(e) => set("capacity", e.target.value)} className={inp} /></Field>
              <Field label="Durée min (mois)"><input type="number" min={1} required value={v.min_duration_months} onChange={(e) => set("min_duration_months", e.target.value)} className={inp} /></Field>
              <Field label="Durée max (mois)"><input type="number" min={1} value={v.max_duration_months || ""} onChange={(e) => set("max_duration_months", e.target.value)} className={inp} /></Field>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="editor-prices">
              {PRICE_FIELDS.map(([k, label]) => (
                <Field key={k} label={label}>
                  <input type="number" min={0} step="0.01" value={v[k] ?? ""} onChange={(e) => set(k, e.target.value)} className={inp} data-testid={`editor-${k}`} />
                </Field>
              ))}
            </div>

            <NeighborhoodFields v={v} set={set} inp={inp} />

            <Field label="Description courte">
              <input value={v.short_description} onChange={(e) => set("short_description", e.target.value)} className={inp} />
            </Field>
            <Field label="Description complète">
              <textarea rows={5} required value={v.description} onChange={(e) => set("description", e.target.value)} className={inp} />
            </Field>

            {/* Photo gallery */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <ImageIcon className="w-4 h-4 text-[#8C6C50]" />
                <span className="text-xs tracking-[0.15em] uppercase font-semibold text-[#666]">Photos ({v.image_urls.length})</span>
              </div>
              <PhotoGrid images={v.image_urls} onChange={(next) => set("image_urls", next)} />
              <div className="flex gap-2">
                <input
                  type="url"
                  placeholder="Ajouter une URL d'image…"
                  value={newImageUrl}
                  onChange={(e) => setNewImageUrl(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addImage(); } }}
                  className={inp + " flex-1"}
                  data-testid="editor-image-input"
                />
                <button type="button" onClick={addImage} className="px-4 py-2.5 rounded-md border border-[#E5E1D8] text-sm hover:bg-[#F5F3ED]" data-testid="editor-add-image-url-btn">Ajouter</button>
                <label className={`px-4 py-2.5 rounded-md bg-[#8C6C50] text-white text-sm flex items-center gap-2 cursor-pointer hover:bg-[#735740] transition-colors ${uploading ? "opacity-60 pointer-events-none" : ""}`} data-testid="editor-upload-images-btn">
                  <Upload className="w-4 h-4" />
                  {uploading ? "Envoi…" : "Téléverser"}
                  <input type="file" accept="image/jpeg,image/png,image/webp,image/gif,image/avif" multiple hidden onChange={uploadImages} data-testid="editor-upload-images-input" />
                </label>
              </div>
              <p className="text-xs text-[#888] mt-2">Si un site bloque l'import, téléversez les photos depuis votre ordinateur ou collez le lien d'une image (clic droit → Copier l'adresse de l'image).</p>
            </div>

            {/* Amenities */}
            <div>
              <span className="text-xs tracking-[0.15em] uppercase font-semibold text-[#666]">Équipements ({v.amenities.length})</span>
              {v.amenities.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2 mb-3">
                  {v.amenities.map((a) => (
                    <span key={a} className="inline-flex items-center gap-1 bg-[#F5F3ED] border border-[#E5E1D8] text-sm px-3 py-1 rounded-full">
                      {a}
                      <button type="button" onClick={() => removeAmenity(a)} className="hover:text-rose-600"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Ex. Wi-Fi"
                  value={newAmenity}
                  onChange={(e) => setNewAmenity(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addAmenity(); } }}
                  className={inp + " flex-1"}
                  data-testid="editor-amenity-input"
                />
                <button type="button" onClick={addAmenity} className="px-4 py-2.5 rounded-md border border-[#E5E1D8] text-sm hover:bg-[#F5F3ED]">Ajouter</button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Nom contact"><input value={v.contact_name} onChange={(e) => set("contact_name", e.target.value)} className={inp} /></Field>
              <Field label="Téléphone"><input value={v.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} className={inp} /></Field>
            </div>
            <ContactEmails emails={v.contact_emails} onChange={(x) => set("contact_emails", x)} />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={v.active} onChange={(e) => set("active", e.target.checked)} /> Visible dans le catalogue
            </label>
            <div className="flex justify-end gap-3 pt-4 border-t border-[#E5E1D8]">
              <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-full text-sm border border-[#E5E1D8]">Annuler</button>
              <button type="submit" className="btn-primary px-6 py-2.5 rounded-full text-sm font-medium" data-testid="editor-save-btn">Enregistrer</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

const inp = "w-full px-3 py-2.5 bg-[#F5F3ED] border border-[#E5E1D8] rounded-md text-sm focus:outline-none focus:border-[#8C6C50]";
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs tracking-[0.15em] uppercase font-semibold text-[#666]">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

const isSending = (c) => !!c?.notifying && Date.now() - new Date(c.notify_started_at || 0).getTime() < 15 * 60000;

function ChoiceStatus({ r, onDone }) {
  const [busy, setBusy] = useState(false);
  const c = r.chosen;
  const sending = isSending(c);
  const resend = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/reservations/${r.id}/resend-choice`, {}, { withCredentials: true });
      toast.success("Envoi en cours à la résidence (jusqu'à 2 min si l'envoi est saturé)");
      onDone();
      setTimeout(onDone, 8000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Envoi impossible");
    } finally {
      setBusy(false);
    }
  };
  if (c.confirmed_at) {
    return <div className="text-xs font-semibold text-emerald-800 mt-1" data-testid={`admin-chosen-confirmed-${r.id}`}>Réservation confirmée par la résidence le {new Date(c.confirmed_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</div>;
  }
  return (
    <div className="flex flex-wrap items-center gap-2 mt-1 text-xs" data-testid={`admin-chosen-pending-${r.id}`}>
      <span className="text-[#8C6C50]">En attente de confirmation de la résidence</span>
      {sending && <span className="text-[#666]" data-testid={`admin-chosen-notifying-${r.id}`}>· envoi à la résidence en cours…</span>}
      {c.notified === 0 && !sending && (
        <button type="button" onClick={resend} disabled={busy} className="px-2.5 py-1 rounded-md bg-[#8C6C50] text-white inline-flex items-center gap-1 hover:bg-[#6B5B49] disabled:opacity-60" data-testid={`admin-resend-choice-${r.id}`}>
          <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} /> {busy ? "Envoi…" : "Renvoyer à la résidence"}
        </button>
      )}
    </div>
  );
}

function ReservationsPanel() {
  const [rows, setRows] = useState([]);
  const [scope, setScope] = useState("current");
  const [forwarding, setForwarding] = useState(null);
  const load = async () => {
    const { data } = await axios.get(`${API}/admin/reservations`, { withCredentials: true });
    setRows(data);
  };
  useEffect(() => { load(); }, []);
  const anySending = rows.some((r) => isSending(r.chosen));
  useEffect(() => {
    if (!anySending) return undefined;
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [anySending]);
  const changeStatus = async (id, status) => {
    await axios.patch(`${API}/admin/reservations/${id}`, { status }, { withCredentials: true });
    toast.success("Statut mis à jour");
    load();
  };
  const shown = rows.filter((r) => isPast(r) === (scope === "past"));
  return (
    <div>
      <h3 className="font-serif text-2xl text-[#1A1A1A]">Demandes ({rows.length})</h3>
      <ScopeTabs scope={scope} setScope={setScope} rows={rows} testid="reservations-scope" />
      <div className="mt-6 space-y-3" data-testid="admin-reservations-list">
        {shown.map((r) => (
          <div key={r.id} className="bg-white border border-[#E5E1D8] p-5 rounded-md" data-testid={`admin-reservation-${r.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 basis-full sm:basis-0 sm:flex-1 break-words">
                <div className="font-serif text-lg text-[#1A1A1A]">{r.listing_title} <span className="text-sm text-[#666]">· {r.listing_city}</span></div>
                <BrandLine r={r} />
                <div className="text-sm text-[#666] mt-1">
                  {r.user_name} &lt;{r.user_email}&gt;
                </div>
                <div className="mt-2 text-sm text-[#2B3029] flex flex-wrap gap-x-6 gap-y-1">
                  <StaySummary r={r} />
                  <span>Personnes : <strong>{r.guests}</strong></span>
                </div>
                {r.message && <p className="mt-2 text-sm text-[#666] italic">« {r.message} »</p>}
                <div className="mt-2 text-xs text-[#666]" data-testid={`admin-reservation-created-${r.id}`}>Demande reçue le {new Date(r.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</div>
                {r.chosen && (
                  <div className="mt-2 text-sm bg-emerald-50 border border-emerald-200 text-emerald-900 rounded px-3 py-2" data-testid={`admin-chosen-${r.id}`}>
                    Choix du client : <strong>{[r.chosen.brand || (r.brands || {})[r.chosen.listing_id], r.chosen.residence !== r.chosen.title ? r.chosen.residence : ""].filter(Boolean).join(" · ") || r.chosen.title}</strong> — {r.chosen.title}
                    {r.chosen.price != null && ` · ${Math.round(r.chosen.price).toLocaleString("fr-FR")} € ${r.chosen.unit === "night" ? "/ nuit" : "/ mois"}`}
                    <span className="text-xs text-emerald-700">{r.chosen.at ? ` · le ${new Date(r.chosen.at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}` : ""}</span>
                    {r.chosen.notified > 0 && <span className="text-xs text-emerald-700"> · résidence prévenue ({r.chosen.notified} contact(s))</span>}
                    {r.chosen.notified === 0 && !isSending(r.chosen) && <span className="text-xs font-semibold text-[#8A3B32]" data-testid={`admin-chosen-not-notified-${r.id}`}> · résidence NON prévenue{r.chosen.no_contact ? " (aucun email de contact)" : ""}</span>}
                    <ChoiceStatus r={r} onDone={load} />
                  </div>
                )}
                <ForwardedInfo key={(r.forwarded || []).length} request={r} kind="reservations" onChange={load} />
              </div>
              <div className="flex flex-col items-end gap-2">
                <select
                  value={r.status}
                  onChange={(e) => changeStatus(r.id, e.target.value)}
                  className="px-3 py-2 bg-[#F5F3ED] border border-[#E5E1D8] rounded-md text-sm"
                  data-testid={`admin-reservation-status-${r.id}`}
                >
                  {STATUS_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
                <ForwardButton onClick={() => setForwarding(r)} testid={`admin-reservation-forward-btn-${r.id}`} />
                <NewAnswerBadge r={r} kind="reservations" onDone={load} />
                <SendResponsesButton r={r} kind="reservations" onDone={load} />
                <ArchiveButton r={r} kind="reservations" onDone={load} />
              </div>
            </div>
          </div>
        ))}
        {shown.length === 0 && <div className="bg-white border border-[#E5E1D8] p-6 text-sm text-[#666] rounded-md">{scope === "past" ? "Aucune demande passée." : "Aucune demande en cours."}</div>}
      </div>
      {forwarding && <ForwardDialog kind="reservations" request={forwarding} onClose={() => setForwarding(null)} onSent={load} />}
    </div>
  );
}


function CustomRequestsPanel() {
  const [rows, setRows] = useState([]);
  const [scope, setScope] = useState("current");
  const [forwarding, setForwarding] = useState(null);
  const load = async () => {
    const { data } = await axios.get(`${API}/admin/custom-requests`, { withCredentials: true });
    setRows(data);
  };
  useEffect(() => { load(); }, []);
  const changeStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/custom-requests/${id}`, { status }, { withCredentials: true });
      toast.success("Statut mis à jour");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Mise à jour impossible");
    }
  };
  return (
    <div>
      <h3 className="font-serif text-2xl text-[#1A1A1A]">Demandes sur mesure ({rows.length})</h3>
      <ScopeTabs scope={scope} setScope={setScope} rows={rows} testid="custom-scope" />
      <div className="mt-6 space-y-3" data-testid="admin-custom-requests-list">
        {rows.filter((r) => isPast(r) === (scope === "past")).map((r) => (
          <div key={r.id} className="bg-white border border-[#E5E1D8] p-5 rounded-md" data-testid={`admin-custom-request-${r.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 basis-full sm:basis-0 sm:flex-1 break-words">
                <div className="font-serif text-lg text-[#1A1A1A]">
                  {r.city} <span className="text-sm text-[#666]">· {(r.areas || []).map((a) => AREA_LABELS[a]).join(", ") || "Emplacement indifférent"}</span>
                </div>
                {r.area_details && <div className="text-sm text-[#2B3029] mt-1">Précisions : {r.area_details}</div>}
                <div className="text-sm text-[#666] mt-1">
                  {r.name} &lt;{r.email}&gt;{r.phone ? ` · ${r.phone}` : ""}
                </div>
                <div className="mt-2 text-sm text-[#2B3029] flex flex-wrap gap-x-6 gap-y-1">
                  <span>Arrivée : <strong>{r.arrival_date}</strong></span>
                  <span>Durée : <strong>{r.duration_months} mois</strong></span>
                  <span>Type : <strong>{(r.apartment_types || []).map((t) => APT_LABELS[t]).join(", ")}</strong></span>
                  <span>Personnes : <strong>{r.guests}</strong></span>
                  <span>Budget max : <strong>{Math.round(r.budget_max)} € / mois</strong></span>
                </div>
                {r.message && <p className="mt-2 text-sm text-[#666] italic">« {r.message} »</p>}
                <div className="mt-2 text-xs text-[#666]" data-testid={`admin-custom-created-${r.id}`}>Demande reçue le {new Date(r.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</div>
                <ForwardedInfo key={(r.forwarded || []).length} request={r} kind="custom-requests" onChange={load} />
              </div>
              <div className="flex flex-col items-end gap-2">
                <select
                  value={r.status}
                  onChange={(e) => changeStatus(r.id, e.target.value)}
                  className="px-3 py-2 bg-[#F5F3ED] border border-[#E5E1D8] rounded-md text-sm"
                  data-testid={`admin-custom-request-status-${r.id}`}
                >
                  {STATUS_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
                <ForwardButton onClick={() => setForwarding(r)} testid={`admin-custom-request-forward-btn-${r.id}`} />
                <NewAnswerBadge r={r} kind="custom-requests" onDone={load} />
                <SendResponsesButton r={r} kind="custom-requests" onDone={load} />
                <ArchiveButton r={r} kind="custom-requests" onDone={load} />
              </div>
            </div>
          </div>
        ))}
        {rows.filter((r) => isPast(r) === (scope === "past")).length === 0 && <div className="bg-white border border-[#E5E1D8] p-6 text-sm text-[#666] rounded-md">{scope === "past" ? "Aucune demande passée." : "Aucune demande sur mesure en cours."}</div>}
      </div>
      {forwarding && <ForwardDialog kind="custom-requests" request={forwarding} onClose={() => setForwarding(null)} onSent={load} />}
    </div>
  );
}
